import { genBannerInteractionClickEventHandlers } from '~~/common/utils/bannerInteractionAnalytics';
import type { BannerInteractionSource } from '~~/common/utils/bannerInteractionAnalytics';
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue';
import { webpFallback } from '~~/common/utils/index';
import { useNuxtApp, useRuntimeConfig } from 'nuxt/app';
import { useUserInterfaceStore } from '~~/common/stores/userInterface.js';
import { LvButton } from '~~/common/components/loginet-vue-shop/index';

/**
 * The CMS payload is not typed repo-wide yet, so this describes only the shape
 * this gallery actually reads out of it.
 */
interface CardMedia {
  contentImage?: {
    url?: string;
  } | null;
}

interface CardContent {
  link?: string | null;
  media?: CardMedia | null;
}

interface GalleryContent {
  id?: string;
  components: CardContent[];
}

// Both galleries spread analyticsBannerInteractionMixin.props alongside their
// own, and the click handlers below are built from those.
interface ExpandableCardGalleryProps extends BannerInteractionSource {
  content: GalleryContent;
}

export function useExpandableCardGallery (props: ExpandableCardGalleryProps) {

  const screenRange = computed(() => {
    return useUserInterfaceStore().mediaQueries;
  });

  const opened = ref(false);
  const visibleCardCount = ref(2);

  const getRowIndexById = inject<((rowId: GalleryContent['id']) => number) | undefined>('getRowIndexById', undefined);

  const isLazyImage = computed(() => {
    // The limit arrives from an env var, so it is a string at runtime.
    const limit = Number(useRuntimeConfig().public.lazyLoadedComponentsLimit);
    if (!getRowIndexById) {
      return true;
    }
    return getRowIndexById(props.content.id) >= limit;
  });

  const visibleContents = computed(() => {
    return props.content.components.slice(0, visibleCardCount.value);

  });
  const hiddenContents = computed(() => {
    return props.content.components.slice(visibleCardCount.value);
  });

  const hiddenContentsHeight = ref(0);
  const hiddenContentsElement = ref<HTMLElement | null>(null);
  const visibleContentsElement = ref<HTMLElement | null>(null);
  const visibleContentsElementItem = ref<HTMLElement[] | null>(null);

  function toggleOpened () {
    opened.value = !opened.value;
  }

  function getContentImageSource (media?: CardMedia | null) {
    return (media?.contentImage
      ? webpFallback([{
        url: media?.contentImage.url || '',
      }])
      : []
    );
  }

  function getComponentAttrs (content: CardContent) {
    const { $hyperlinkComputeBindFromUrl } = useNuxtApp();

    return content.link
      ? {
        is: LvButton,
        attrs: {
          ...$hyperlinkComputeBindFromUrl(content.link),
          stretch: true,
          styling: 'link-primary-version2',
        },
        handlers: genBannerInteractionClickEventHandlers(props),
      }
      : {
        is: 'div',
        attrs: null,
        handlers: {},
      }
    ;
  }

  function calculateVisibleCardCount () {
    if (!visibleContentsElement.value) {
      return;
    }
    const width = visibleContentsElement.value.offsetWidth;
    const cardWidth = screenRange.value['tablet-min'] ? 160 : 136;
    const gap = 12;
    visibleCardCount.value = Math.floor((width + gap) / (cardWidth + gap));

    const height = visibleContentsElementItem.value?.[0]?.offsetHeight;
    if (height === undefined) {
      return;
    }
    hiddenContentsHeight.value = (height + gap) * Math.ceil(hiddenContents.value.length / visibleCardCount.value) - gap;
  }

  onMounted(() => {
    calculateVisibleCardCount();

    const { $awEvents } = useNuxtApp();
    $awEvents.addEvent('throttled-resize', calculateVisibleCardCount);

  });
  onBeforeUnmount(() => {
    const { $awEvents } = useNuxtApp();
    $awEvents.removeEvent('throttled-resize', calculateVisibleCardCount);
  });

  return {
    opened,
    visibleContents,
    hiddenContents,
    hiddenContentsHeight,
    hiddenContentsElement,
    visibleContentsElement,
    visibleContentsElementItem,
    isLazyImage,
    toggleOpened,
    getContentImageSource,
    getComponentAttrs,
  };
}
